import { concat, set } from 'lodash';
import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components';
import FormDrawer from '~/components/FormDrawer';
import { useGetApplication } from '~/controllers/wizard';
import { PersonAddIcon } from '~/img';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { FormItemInjectedProps } from '~/types/FormItemInjectedProps';
import { Person } from '~/types/Person';
import buildNamePath from '~/utils/buildNamePath';
import { hasSoftDeclineFields } from '~/utils/softDeclinesFields';

import { useContactAdd } from '~/utils/useContactAdd';
import { MatchingContactsModal } from '../MatchingContactsModal';
import { isShareholderEditButtonDisabled } from '../ShareholdersMultiple/utils';
import AuthorizedRepresentativeForm, { formName } from './AuthorizedRepresentativeForm';

interface AuthorizedRepresentativeAddProps extends Partial<FormItemInjectedProps<Person[]>> {
  setSourceOfEditing: Dispatch<SetStateAction<string>>;
}

const AuthorizedRepresentativeAdd: React.FC<AuthorizedRepresentativeAddProps> = (props) => {
  const { value, onChange, setSourceOfEditing } = props;
  const { t } = useTranslation('fillApplication');

  const newauthorizedRepresentativeIndex = value?.length ?? 0;
  const applicationQuery = useGetApplication();
  const hasIndividualsValidationWarnings = hasSoftDeclineFields(applicationQuery.data, 'authorizedRepresentative[]');

  const {
    drawerOpen,
    setDrawerOpen,
    modalOpen,
    onCancelModal,
    onCloseDrawer,
    contactState,
    setSelectedContact,
    handleCreateNewContact,
    handleSelectContact,
    onFinish,
  } = useContactAdd({
    sourceOfEditingValue: 'authorizedRepresentative',
    setSourceOfEditing,
    onUpdate: (contact: Person) => {
      onChange?.([...(value || []), contact]);
    },
    getNewContact: (newValues: ApplicationPayload) =>
      newValues.authorizedRepresentative?.[newauthorizedRepresentativeIndex] as Person,
    getNewContactData: (newContact: Person) => newContact,
    setContactIds: (newContact: Person, contactId: string) => {
      set(newContact, 'matchContactId', contactId);
      set(newContact, 'contactId', contactId);
    },
    setNewContactUserRoles: (newContact: Person, userRoles: Array<string>) => {
      set(newContact, 'userRoles', concat(newContact.userRoles, userRoles));
    },
  });

  return (
    <>
      <Button
        id="addNewAuthorizedRepresentativeButton"
        extraLarge
        adaptive
        type="default"
        icon={<PersonAddIcon />}
        disabled={isShareholderEditButtonDisabled(applicationQuery.data, hasIndividualsValidationWarnings)}
        onClick={() => setDrawerOpen(true)}
      >
        {t('Add New Individual Authorized Representative')}
      </Button>
      <FormDrawer
        id="authorizedRepresentativeAddDrawer"
        open={drawerOpen}
        onClose={onCloseDrawer}
        title={t('Add New Authorized Representative')}
        formName={formName}
      >
        <MatchingContactsModal
          open={modalOpen}
          title={t(
            'This contact matches existing contacts. If you wish to update any of the contacts below, please select the contact you want to update, or create a new contact',
          )}
          listData={contactState.matchingContactList}
          selectedContact={contactState.selectedContact}
          onCancel={onCancelModal}
          setSelectedContact={setSelectedContact}
          handleCreateNewContact={handleCreateNewContact}
          handleSelectContact={handleSelectContact}
        />
        <AuthorizedRepresentativeForm
          onFinish={onFinish}
          initialValues={{ authorizedRepresentative: value }}
          n={buildNamePath(['authorizedRepresentative', newauthorizedRepresentativeIndex])}
        />
      </FormDrawer>
    </>
  );
};

export default AuthorizedRepresentativeAdd;
